import React, { useState } from 'react';
import { Box, Typography, Paper, Button, Divider } from '@mui/material';
import { Category } from 'models/organization';
import { useTranslation } from 'react-i18next';
import AddMainCategoryDialog from './add-main-category-dialog.component';
import { saveMainCategoryTemplate, deleteMainCategoryTemplate } from 'apollo/templates/action-sets';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type MainCategoryProps = {
    categories: Category[];
    actionSetId: string;
    reFetchActionSet?: (actionSetId: string) => void;
};

const MainCategory: React.FC<MainCategoryProps> = ({ categories, actionSetId, reFetchActionSet }) => {
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const [category, setCategory] = useState({} as Category);
    const [addMainCategoryDialogOpen, setAddMainCategoryDialogOpen] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();

    const [saveMainCategory] = useMutation(saveMainCategoryTemplate, {
        onCompleted: () => {
            if (reFetchActionSet) {
                reFetchActionSet(actionSetId);
            }
            closeAddMainCategoryDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [deleteMainCategory] = useMutation(deleteMainCategoryTemplate, {
        onCompleted: () => {
            if (reFetchActionSet) {
                reFetchActionSet(actionSetId);
            }
            closeAddMainCategoryDialog();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const onAddEditCategory = (category?: Category) => {
        setCategory(category || ({ actionSet: actionSetId } as Category));
        setAddMainCategoryDialogOpen(true);
    };

    const closeAddMainCategoryDialog = () => {
        setAddMainCategoryDialogOpen(false);
    };

    const onDeleteCategory = (categoryId?: string) => {
        deleteMainCategory({
            variables: {
                mainCategoryId: categoryId,
            },
        });
    };

    const onSaveCategory = (category: Category) => {
        saveMainCategory({
            variables: {
                _id: category._id,
                name: category.name,
                instructions: category.instructions,
                actionSetId: actionSetId,
            },
        });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {translate('client-directory:MAIN_CATEGORY')}
            </Typography>

            <Divider />

            <Box mt={2} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {categories.map((category, i) => (
                    <Paper
                        key={i}
                        variant="outlined"
                        onClick={() => onAddEditCategory(category)}
                        sx={{
                            p: 1.5,
                            minWidth: 150,
                            cursor: 'pointer',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        {i + 1}. {category.name}
                    </Paper>
                ))}

                <Button
                    onClick={() => onAddEditCategory()}
                    sx={{
                        height: 'fit-content',
                        minWidth: 150,
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    + {translate('common:ADD')}
                </Button>
            </Box>
            <AddMainCategoryDialog
                open={addMainCategoryDialogOpen}
                onClose={closeAddMainCategoryDialog}
                category={category}
                onSave={onSaveCategory}
                onDelete={onDeleteCategory}
            />
        </Box>
    );
};

export default MainCategory;
